import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getSubscriptionTypeFromToken, getUserRoleFromToken } from '../utils/jwt';

const EntitlementsContext = createContext(null);

export function EntitlementsProvider({ children }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entitlements, setEntitlements] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(getSubscriptionTypeFromToken(token));
  const [aiCredits, setAiCredits] = useState(null);
  const [limits, setLimits] = useState(null);

  const role = useMemo(() => getUserRoleFromToken(token), [token]);

  const refresh = useCallback(async () => {
    const latestToken = typeof window !== 'undefined' ? localStorage.getItem('token') : token;
    if (!latestToken) {
      setEntitlements(null);
      setLimits(null);
      setAiCredits(null);
      setSubscriptionType('free');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [entRes, limitsRes] = await Promise.all([
        fetch('/api/entitlements/me', { headers: { Authorization: `Bearer ${latestToken}` } }),
        fetch('/api/limits/me', { headers: { Authorization: `Bearer ${latestToken}` } }),
      ]);

      const entJson = await entRes.json().catch(() => ({}));
      if (!entRes.ok) throw new Error(entJson?.message || 'Failed to load entitlements');

      const limitsJson = await limitsRes.json().catch(() => ({}));
      if (!limitsRes.ok) throw new Error(limitsJson?.message || 'Failed to load limits');

      setEntitlements(entJson?.entitlements || null);
      setSubscriptionType(entJson?.subscriptionType || getSubscriptionTypeFromToken(latestToken));
      setAiCredits(entJson?.aiCredits || null);
      setLimits(limitsJson || null);
    } catch (e) {
      setError(e.message || 'Failed to load entitlements');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => {
    return {
      loading,
      error,
      role,
      subscriptionType,
      entitlements,
      aiCredits,
      limits,
      refresh,
    };
  }, [aiCredits, entitlements, error, limits, loading, refresh, role, subscriptionType]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) throw new Error('useEntitlements must be used within an EntitlementsProvider');
  return ctx;
}
